<template>
  <PageSideNav
    title="Overview Navigation"
    v-model:activeItem="activeItem"
    :navigationItems="navigationItems"
    :initiallyOpenGroups="initialOpenGroups"
    @itemClick="handleNavigation"
  />
</template>

<script setup lang="ts">
  import { ref, computed } from 'vue';
  import { useRouter, useRoute } from 'vue-router';
  import PageSideNav from '../shared/PageSideNav.vue';

  // Define component props
  defineProps<{
    title?: string; // Optional title for the navigation list
  }>();

  const router = useRouter();
  const route = useRoute();

  // Track the currently active item
  const activeItem = ref('overview');

  // Keep the grade group open if a grade is active
  const initialOpenGroups = computed(() => {
    return activeItem.value.startsWith('grade-') ? ['reports-by-grade'] : [];
  });

  // Define the navigation structure for this specific overview page
  const navigationItems = computed(() => [
    {
      title: 'Overview',
      value: 'overview',
      route: {
        name: 'report-overview',
        params: { id: route.params.id as string },
      },
    },
    {
      title: 'ELA K-2',
      value: 'ela-k-2',
      indentChildren: true,
      children: [
        {
          title: 'K-2 Alignment & Usability',
          value: 'k2-alignment',
          // TODO: Add route or scroll target
        },
        {
          title: 'K-2 Science of Reading',
          value: 'k2-science',
        },
        {
          title: 'K-2 Additional Data',
          value: 'k2-data',
        },
      ],
    },
    {
      title: 'ELA 3-6',
      value: 'ela-3-6',
      indentChildren: true,
      children: [
        {
          title: '3-6 Alignment & Usability',
          value: '36-alignment',
        },
        {
          title: '3-6 Science of Reading',
          value: '36-science',
        },
        {
          title: '3-6 Additional Data',
          value: '36-data',
        },
      ],
    },
    {
      title: 'Reports by Grade',
      value: 'reports-by-grade',
      isH4: true,
      isGroup: true,
      borderTop: true,
      children: [
        {
          title: 'Kindergarten',
          value: 'grade-k',
          route: {
            name: 'report-grade-overview',
            params: { grade: 'kindergarten' },
          },
        },
        {
          title: '1st Grade',
          value: 'grade-1',
          route: {
            name: 'report-grade-overview',
            params: { grade: 'first-grade' },
          },
        },
        {
          title: '2nd Grade',
          value: 'grade-2',
          route: {
            name: 'report-grade-overview',
            params: { grade: 'second-grade' },
          },
        },
        {
          title: '3rd Grade',
          value: 'grade-3',
          route: {
            name: 'report-grade-overview',
            params: { grade: 'third-grade' },
          },
        },
        {
          title: '4th Grade',
          value: 'grade-4',
          route: {
            name: 'report-grade-overview',
            params: { grade: 'fourth-grade' },
          },
        },
        {
          title: '5th Grade',
          value: 'grade-5',
          route: {
            name: 'report-grade-overview',
            params: { grade: 'fifth-grade' },
          },
        },
        {
          title: '6th Grade',
          value: 'grade-6',
          route: {
            name: 'report-grade-overview',
            params: { grade: 'sixth-grade' },
          },
        },
        {
          title: '7th Grade',
          value: 'grade-7',
          route: {
            name: 'report-grade-overview',
            params: { grade: 'seventh-grade' },
          },
        },
      ],
    },
  ]);

  const handleNavigation = (item: any) => {
    // Update active state first
    activeItem.value = item.value;

    // Then handle routing if available
    if (item.route?.name) {
      router.push({
        name: item.route.name,
        params: {
          ...item.route.params,
          id: route.params.id as string,
        },
      });
    } else {
      // Placeholder for non-route clicks (e.g., scroll to anchor)
    }
  };
</script>

<!-- Removed scoped style block as styles are handled by PageSideNav -->
